import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import type { ModuleCard as ModuleCardData } from '../dashboards.api';
import { formatValue, toneOf } from '../dashboards.utils';

/**
 * One module on Home: icon, name and up to four headline numbers. The whole card opens the
 * module dashboard (the server only sends modules the user can open).
 */
export default function ModuleCard({ card }: { card: ModuleCardData }) {
  const kpis = (card.kpis ?? []).slice(0, 4);
  return (
    <Box
      component={RouterLink}
      to={card.link || `/dashboards/${card.module}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '100%',
        p: 2,
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 2,
        bgcolor: 'background.paper',
        color: 'text.primary',
        textDecoration: 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover, &:focus-visible': { borderColor: 'primary.light', boxShadow: 2, outline: 'none' },
        '&:hover .module-card-go': { transform: 'translateX(2px)' },
      }}
    >
      <Stack direction='row' alignItems='center' gap={1.25}>
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            flexShrink: 0,
          }}
        >
          <Icon icon={card.icon || 'mdi:view-dashboard-variant-outline'} width={20} />
        </Box>
        <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: '1rem' }} noWrap>
          {card.label}
        </Typography>
        <Box className='module-card-go' sx={{ display: 'inline-flex', color: 'text.secondary', transition: 'transform 0.15s' }}>
          <Icon icon='mdi:chevron-right' width={20} />
        </Box>
      </Stack>

      {kpis.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          Open the dashboard
        </Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
          {kpis.map((k) => {
            const tone = toneOf(k.tone);
            return (
              <Stack key={k.key} gap={0.25} sx={{ minWidth: 0 }}>
                <Typography variant='caption' color='text.secondary' noWrap title={k.label}>
                  {k.label}
                </Typography>
                <Stack direction='row' alignItems='center' gap={0.5}>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.25, whiteSpace: 'nowrap' }}>
                    {formatValue(k.value, k.unit)}
                  </Typography>
                  {tone.icon && (
                    <Box component='span' role='img' aria-label={tone.label} sx={{ display: 'inline-flex', color: tone.color }}>
                      <Icon icon={tone.icon} width={15} />
                    </Box>
                  )}
                </Stack>
              </Stack>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export function ModuleCardSkeleton() {
  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 2 }}>
      <Stack direction='row' alignItems='center' gap={1.25} sx={{ mb: 1.5 }}>
        <Skeleton variant='rounded' width={36} height={36} />
        <Skeleton variant='text' width='50%' />
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        {[0, 1, 2, 3].map((i) => (
          <Box key={i}>
            <Skeleton variant='text' width='70%' />
            <Skeleton variant='text' width='45%' height={28} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
